import { useState, useEffect, useRef } from "react";
import "./App.css";
import {
  User,
  FileText,
  House,
  X,
  FileUser,
  Menu,
  HelpCircle,
  LogOut,
  CircleX,
  Settings2,
  ArrowLeft,
  BrainCircuit,
  Bookmark,
} from "lucide-react";
import {
  registerUser,
  loginUser,
  verifyToken,
  logoutUser,
  checkResumeExists,
  parseResume,
  saveProfile,
  saveResumeData,
  deleteResume,
  saveJobPreferences,
  applyToJob,
  validateJob,
  getJobDetailsFromText,
  generateTailoredResume,
  generateCoverLetter,
  completeApplicationWithAdditionalFields,
  saveJobApplication,
  deleteJobApplication,
  getAllApplications,
  getSavedJobs,
  getUserProfile,
  deleteAllData,
  verifyCode,
  sendVerificationEmail,
  sendErrorReport,
  refreshMatchScoresForAll,
  createCheckoutSession,
  saveSubscription,
  cancelSubscription,
} from "./services/api";

// Import all screen components
import LoginScreen from "./LoginScreen";
import StartScreen from "./StartScreen";
import PersonalInfoScreen from "./PersonalInfoScreen";
import SettingsScreen from "./SettingsScreen";
import HomeScreen from "./HomeScreen";
import YourJobsScreen from "./YourJobsScreen";
import ApplicationHistoryScreen from "./ApplicationHistoryScreen";
import ProfileScreen from "./ProfileScreen";
import TailorResumeScreen from "./TailorResumeScreen";
import HelpScreen from "./HelpScreen";
import ResetPasswordScreen from "./ResetPasswordScreen";
import LegalScreen from "./LegalScreen";
import SubscriptionPlansScreen from "./subscriptionPlans";
import ReferralScreen from "./referralScreen";
import SkillGapScreen from "./SkillGapScreen";
import FeaturesScreen from "./FeaturesScreen";
import { ToastProvider, useToast } from "./components/ToastContext";
import MascotAssistant from "./components/MascotAssistant";
import { getUserMessage } from "./services/api";

const splitCompanyNames = (company = "") =>
  company
    .split(";")
    .map((name) => name.trim())
    .filter(Boolean);

const JobApplicationApp = () => {
  const toast = useToast();
  const [currentScreen, setCurrentScreen] = useState(null);
  const [userEmail, setUserEmail] = useState("");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authError, setAuthError] = useState("");
  const [resumeExists, setResumeExists] = useState(false);
  const [userData, setUserData] = useState({});
  const [resumeData, setResumeData] = useState({});
  const [jobList, setJobList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showJobUrlPopup, setShowJobUrlPopup] = useState(false);
  const [customJobUrl, setCustomJobUrl] = useState("");
  const [jobValidation, setJobValidation] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [showMissingFieldsPopup, setShowMissingFieldsPopup] = useState(false);
  const [missingFieldsData, setMissingFieldsData] = useState(null);
  const [currentJobUrl, setCurrentJobUrl] = useState("");
  const [tailoredResume, setTailoredResume] = useState(null);
  const [tailoredResumeDocx, setTailoredResumeDocx] = useState(null);
  const [tailoredChanges, setTailoredChanges] = useState(null);
  const [jobDescription, setJobDescription] = useState("");
  const [applicationDetails, setApplicationDetails] = useState({});
  const [ats, setAts] = useState(0);
  const [applicationHistory, setApplicationHistory] = useState([]);
  const [isLoadingApplicationHistory, setIsLoadingApplicationHistory] =
    useState(false);
  const [isLoadingJobs, setIsLoadingJobs] = useState(false);
  const [savedJobCompanies, setSavedJobCompanies] = useState([]);
  const [showErrorReportDialog, setShowErrorReportDialog] = useState(false);
  const [errorMsg, setErrorMsg] = useState({ subject: "", message: "" });
  const [errorReportConsent, setErrorReportConsent] = useState(null);
  const [errorReportForm, setErrorReportForm] = useState({
    always_yes: false,
    ask_me_later: false,
    always_no: false,
  });
  const [isGeneratingCoverLetter, setIsGeneratingCoverLetter] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isRefreshingScores, setIsRefreshingScores] = useState(false);
  const [userStats, setUserStats] = useState({
    total_applications: 0,
    total_interviews: 0,
    total_jobs_saved: 0,
  });
  const [subscriptionCounts, setSubscriptionCounts] = useState({
    resumes_used: 0,
    resumes_limit: 0,
    cover_letters_generated: 0,
    cover_letters_limit: 0,
    end_date: null,
    free_trial_used: false,
    batch_limit: 0,
    billing_cycle_end: null,
    tier: null,
  });
  const activeUserEmailRef = useRef("");
  const applicationHistoryRequestRef = useRef(0);

  useEffect(() => {
    activeUserEmailRef.current = userEmail;
  }, [userEmail]);

  const loadApplicationHistoryForUser = async (
    email,
    context = "Application history load",
  ) => {
    if (!email) return;

    const requestId = applicationHistoryRequestRef.current + 1;
    applicationHistoryRequestRef.current = requestId;
    setIsLoadingApplicationHistory(true);

    try {
      const res = await getAllApplications(email);
      if (
        requestId === applicationHistoryRequestRef.current &&
        activeUserEmailRef.current === email
      ) {
        setApplicationHistory(res?.applications || []);
      }
    } catch (err) {
      if (
        requestId === applicationHistoryRequestRef.current &&
        activeUserEmailRef.current === email
      ) {
        handleErrors(context, err);
      }
    } finally {
      if (
        requestId === applicationHistoryRequestRef.current &&
        activeUserEmailRef.current === email
      ) {
        setIsLoadingApplicationHistory(false);
      }
    }
  };
  const [loadingProgress, setLoadingProgress] = useState("");
  const [loadingMessage, setLoadingMessage] = useState("");
  const PDF_MIME_TYPE = "application/pdf";
  const DOCX_MIME_TYPE =
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

  // Define public routes
  const publicRoutes = [
    "",
    "login",
    "features",
    "help",
    "reset",
    "terms",
    "privacy",
    "referral",
  ];

  // Mock job data - only Easy and Medium applications
  const sampleJobs = [
    {
      id: 1,
      title: "Senior iOS Engineer - Generative AI",
      company: "Apple",
      location: "Cupertino, CA",
      salary: "$150k - $200k",
      matchScore: 95,
      difficulty: "Easy",
      url: "https://jobs.apple.com/en-us/details/200597761/senior-ios-engineer-generative-ai-experiences-software",
    },
    {
      id: 2,
      title: "Software Engineer - Full Stack",
      company: "Google",
      location: "Mountain View, CA",
      salary: "$140k - $180k",
      matchScore: 88,
      difficulty: "Medium",
      url: "https://careers.google.com/jobs/results/",
    },
  ];

  const handleIsValidUser = async (email = userEmail) => {
    const hasResume = await checkResumeExists(email);
    setResumeExists(hasResume.exists);
    return hasResume.exists;
  };

  // Protected navigation function
  const navigateTo = async (
    screen,
    skipAuthCheck = false,
    verifiedEmail = null,
    options = {},
  ) => {
    if (publicRoutes.includes(screen)) {
      setCurrentScreen(screen);
      window.history.pushState({}, "", `/${screen}`);
      return;
    }

    if (!skipAuthCheck) {
      if (!isAuthenticated && !verifiedEmail) {
        toast.info("Please log in to access this page.");
        setCurrentScreen("login");
        window.history.pushState({}, "", "/login");
        return;
      }

      const email = verifiedEmail || userEmail;

      if (subscriptionCounts?.tier === null) {
        const userInfo = await getUserProfile(email, false, false, false, true);
        if (userInfo.stats) {
          setUserStats(userInfo.stats);
        }
        if (userInfo.subscription && userInfo.subscription.tier) {
          const {
            resumes_used,
            resumes_limit,
            cover_letters_generated,
            cover_letters_limit,
            end_date,
            free_trial_used,
            batch_limit,
            billing_cycle_end,
            tier,
          } = userInfo.subscription;
          setSubscriptionCounts({
            resumes_used: resumes_used,
            resumes_limit: resumes_limit,
            cover_letters_generated: cover_letters_generated,
            cover_letters_limit: cover_letters_limit,
            end_date: end_date,
            free_trial_used: free_trial_used,
            batch_limit: batch_limit,
            billing_cycle_end: billing_cycle_end,
            tier: tier,
          });
          if (
            !userInfo.subscription.tier ||
            userInfo.subscription.tier === "expired"
          ) {
            setCurrentScreen("subscriptionPlans");
            window.history.pushState({}, "", "/subscriptionPlans");
            return;
          }
        } else {
          // No subscription or empty subscription — redirect to plans
          setCurrentScreen("subscriptionPlans");
          window.history.pushState({}, "", "/subscriptionPlans");
          return;
        }
      }

      const hasResume = await handleIsValidUser(email);
      if (!hasResume) {
        setCurrentScreen("start");
        window.history.pushState({}, "", "/start");
        return;
      }
    }

    if (screen === "tailor") resetTailorState();
    setCurrentScreen(screen);
    window.history.pushState({}, "", `/${screen}`);
    if (screen === "tailor" && options?.jobDescription != null) {
      setJobDescription(options.jobDescription);
    }
  };

  const navigateBack = async () => {
    window.history.back();
  };

  useEffect(() => {
    const handlePopState = () => {
      const screen = window.location.pathname.replace("/", "");
      setCurrentScreen(screen || "login");
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  // Sync screen with URL on mount and auth changes
  useEffect(() => {
    const requestedScreen = window.location.pathname.replace("/", "");
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get("token");
    const storedToken = localStorage.getItem("auth_token");

    // If there's a token, always run init regardless of the path
    if (!token && publicRoutes.includes(requestedScreen)) {
      setCurrentScreen(requestedScreen || "login");
      return;
    }

    // No stored token and no token in URL → user isn't authenticated.
    // Show login immediately instead of a spinner that will only redirect there.
    if (!token && !storedToken) {
      setCurrentScreen("login");
      return;
    }
    // Otherwise leave currentScreen null (loading spinner) while init() verifies.

    const init = async () => {
      const error = urlParams.get("error");

      if (error) {
        setAuthError("Authentication failed. Please try again.");
        setCurrentScreen("login");
        window.history.replaceState({}, document.title, "/");
        return;
      }

      const hadToken = !!localStorage.getItem("auth_token");

      if (token) {
        localStorage.setItem("auth_token", token);
        window.history.replaceState({}, document.title, "/");
      }

      const email = await handleVerifyAuthentication();
      if (email) {
        loadApplicationHistoryForUser(email, "Initial application load");
        await navigateTo(requestedScreen || "home", false, email);
      } else {
        if (hadToken)
          setAuthError("Your login has expired. Please login again.");
        navigateTo("login", true);
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============================================
  // AUTHENTICATION HANDLERS
  // ============================================

  const handleVerifyAuthentication = async () => {
    const token = localStorage.getItem("auth_token");
    if (!token) {
      return null; // just return, don't navigate
    }

    const result = await verifyToken();

    if (result.success && result.valid) {
      setIsAuthenticated(true);
      setUserEmail(result.user.email);
      activeUserEmailRef.current = result.user.email;
      setUserData((prev) => ({
        ...prev,
        profileData: { ...prev.profileData, ...result.user.profile },
      }));
      setErrorReportConsent(
        result.user["profile_data"]["error_report_consent"] ?? null,
      );
      return result.user.email;
    } else {
      localStorage.removeItem("auth_token");
      return null;
    }
  };

  const handleRegister = async (email, password, firstName, lastName) => {
    setAuthError("");
    setIsLoading(true);

    const result = await registerUser(email, password, firstName, lastName);

    if (result.success) {
      setIsAuthenticated(true);
      setUserEmail(email);
      setUserData((prev) => ({
        ...prev,
        profileData: {
          ...prev.profileData,
          firstName,
          lastName,
          email,
        },
      }));
    } else {
      setAuthError(result.error);
    }

    setIsLoading(false);
    return result.success;
  };

  const handleLogin = async (email, password) => {
    setAuthError("");
    setIsLoading(true);

    const result = await loginUser(email, password);

    if (result.success) {
      // Verify and get user data
      const verifiedEmail = await handleVerifyAuthentication();
      if (verifiedEmail) {
        loadApplicationHistoryForUser(
          verifiedEmail,
          "Initial application load",
        );
        await navigateTo("home", false, verifiedEmail);
      } else {
        setAuthError("We encountered an issue. Please try logging in again.");
      }
    } else {
      setAuthError(result.error);
    }

    setIsLoading(false);
    return result.success;
  };

  const handleLogout = async () => {
    await logoutUser();
    resetTailorState();
    resetSession();
    navigateTo("login");
  };

  const resetSession = () => {
    applicationHistoryRequestRef.current += 1;
    activeUserEmailRef.current = "";
    setIsAuthenticated(false);
    setUserEmail("");
    setUserData({});
    setApplicationHistory([]);
    setIsLoadingApplicationHistory(false);
    setUserStats({
      total_applications: 0,
      total_interviews: 0,
      total_jobs_saved: 0,
    });
    setResumeExists(false);
    setJobList([]);
    setResumeData({});
    setShowJobUrlPopup(false);
    setCurrentJobUrl("");
    setJobValidation(null);
    setIsValidating(false);
    setAts(0);
    setSavedJobCompanies([]);
    setErrorMsg({ subject: "", message: "" });
    setShowErrorReportDialog(false);
    setSubscriptionCounts({
      resumes_used: 0,
      resumes_limit: 0,
      cover_letters_generated: 0,
      cover_letters_limit: 0,
      end_date: null,
      free_trial_used: false,
      batch_limit: 0,
      billing_cycle_end: null,
      tier: null,
    });
  };

  const loadSavedJobs = async () => {
    if (!userEmail) return;

    setIsLoadingJobs(true);

    try {
      const response = await getSavedJobs(userEmail);
      if (response.success) {
        setJobList(response.saved_jobs);
        setSavedJobCompanies([
          ...new Set(
            response.saved_jobs.flatMap((job) =>
              splitCompanyNames(job.company),
            ),
          ),
        ]);
      }

      // Check if resume exists in database
      const userInfo = await getUserProfile(userEmail, true, true, false, true);
      setResumeData(userInfo.resumeData || {});
    } catch (error) {
      console.error("Failed to load saved jobs");
      handleErrors("Error loading saved jobs", error);
    } finally {
      setIsLoadingJobs(false);
    }
  };

  const refreshMatchScores = async () => {
    try {
      setIsLoadingJobs(true);
      await refreshMatchScoresForAll(userEmail);
      await loadSavedJobs();
    } catch (error) {
      console.error("Error refreshing match scores for all");
      handleErrors("Error refreshing match scores for all", error);
      toast.error(getUserMessage(error));
    } finally {
      setIsLoadingJobs(false);
    }
  };

  const handleErrors = async (subject, error_obj) => {
    if (errorReportConsent === null) {
      setErrorMsg({ subject: subject, message: error_obj.message });
      setShowErrorReportDialog(true);
    } else {
      if (errorReportConsent === true) {
        await sendErrorReport(
          userEmail,
          subject,
          error_obj.message,
          errorReportConsent,
        );
      }
    }
  };

  const handleSendErrorReport = async () => {
    let consent = null;
    if (errorReportForm.always_yes) {
      consent = true;
      setErrorReportConsent(true);
    } else if (errorReportForm.always_no) {
      consent = false;
      setErrorReportConsent(false);
    } else {
      consent = null;
      setErrorReportConsent(null);
    }

    sendErrorReport(userEmail, errorMsg.subject, errorMsg.message, consent);
  };

  // ============================================
  // JOB APPLICATION HANDLERS
  // ============================================

  // Job URL validation handler
  const handleJobUrlValidation = async () => {
    if (!customJobUrl.trim()) return;

    setIsValidating(true);
    setJobValidation(null);

    try {
      var result = await validateJob(customJobUrl);
      if (!result.details.description) {
        result = { ...result, details: { ...result.details, valid: false } };
      }
      setJobValidation(result);
      setJobDescription(result.details.description);
    } catch (error) {
      toast.error(
        "We couldn't retrieve the job description. Please try pasting the description manually.",
      );
      handleErrors("Job URL Validation", error);
      setJobValidation({});
    } finally {
      setIsValidating(false);
    }
  };

  // Reset popup state
  const resetJobUrlPopup = () => {
    setShowJobUrlPopup(false);
    setJobValidation(null);
    setIsValidating(false);
    setCustomJobUrl("");
  };

  // Handle custom job application
  // const handleCustomJobApplication = async () => {
  //   if (!customJobUrl.trim()) {
  //     alert("Please enter a job URL");
  //     return;
  //   }

  //   await handleJobApplication(customJobUrl);

  //   // Only reset if not showing missing fields popup
  //   if (!showMissingFieldsPopup) {
  //     resetJobUrlPopup();
  //   }
  // };

  // const handleGetJobDetails = async (jobUrl) => {
  //   try {
  //     const response = await getJobDetails(jobUrl);
  //     setApplicationDetails({ ...response, url: jobUrl });
  //     if (response.description?.includes("Error extracting job description")) {
  //       setJobDescription("");
  //       setApplicationDetails({});
  //       throw new Error("Failed to extract job description");
  //     }
  //     const jobDesc = response.description;
  //     setJobDescription(jobDesc);
  //     return response;
  //   } catch (error) {
  //     console.error("Error fetching job details:", error);
  //     throw error;
  //   }
  // };

  const handleGetJobDetailsFromText = async (jobText) => {
    try {
      const response = await getJobDetailsFromText(jobText);
      return response;
    } catch (error) {
      console.error("Error fetching job details from text");
      handleErrors("Get Job Details From Text", error);
      throw error;
    }
  };

  const handleJobApplication = async (jobUrl) => {
    setIsLoading(true);
    setCurrentJobUrl(jobUrl); // Store current job URL

    try {
      const result = await applyToJob(jobUrl, userEmail);

      if (result.requires_user_input && result.missing_fields_data) {
        setMissingFieldsData(result.missing_fields_data);
        setShowMissingFieldsPopup(true);
        return; // Don't show success/error message yet
      }

      // Handle normal success/failure
      if (result.success) {
        toast.success("Successfully applied to the job.");

        // Add to application history
        const newApplication = {
          id: Date.now(),
          url: jobUrl,
          applied: true,
          application_date: new Date().toISOString(),
          status: "applied",
          result: result,
        };
        setApplicationHistory((prev) => [newApplication, ...prev]);
      } else {
        toast.error(
          "We couldn't apply to that job automatically. Please try applying directly on the company site.",
        );
      }
    } catch (error) {
      console.error("Job application error");
      toast.error(getUserMessage(error));
      handleErrors("Handle Job Application", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteApplication = async (appId) => {
    setIsLoading(true);
    try {
      await deleteJobApplication(appId, userEmail);
    } catch (error) {
      console.error("Error deleting job application");
      handleErrors("Job Application Deletion", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const handleMissingFieldsSubmit = async (additionalFields) => {
    setIsLoading(true);
    try {
      const result = await completeApplicationWithAdditionalFields(
        currentJobUrl,
        userEmail,
        additionalFields,
      );

      setShowMissingFieldsPopup(false);
      setMissingFieldsData(null);

      if (result.success) {
        toast.success("Application completed successfully.");

        // Add to application history
        const newApplication = {
          id: Date.now(),
          url: currentJobUrl,
          applied: true,
          application_date: new Date().toISOString(),
          status: "applied",
          result: result,
          additionalFields: additionalFields,
        };
        setApplicationHistory((prev) => [newApplication, ...prev]);
      } else {
        toast.error(
          "We couldn't complete the application. Please try applying directly on the company site.",
        );
      }

      // Reset popup state
      resetJobUrlPopup();
    } catch (error) {
      console.error("Complete application error");
      toast.error(getUserMessage(error));
      handleErrors("Submit Missing Fields for Application", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMissingFieldsCancel = () => {
    setShowMissingFieldsPopup(false);
    setMissingFieldsData(null);
    setCurrentJobUrl("");
    resetJobUrlPopup();
    toast.info("Application cancelled.");
  };

  const handleSaveJobApplication = async (appDetails = applicationDetails) => {
    try {
      setApplicationDetails(appDetails);
      const applicationResult = await saveJobApplication(userEmail, appDetails);
      return applicationResult["application"];
    } catch (error) {
      console.error("Error saving job application");
      handleErrors("Save Job Application", error);
      throw error;
    }
  };

  // ============================================
  // RESUME HANDLERS
  // ============================================

  // Resume upload and parsing handlers
  const handleResumeUpload = (e) => {
    if (e.target.files[0]) {
      const file = e.target.files[0];
      setResumeData({
        ...resumeData,
        resume: file,
      });
      return file;
    }
  };

  const handleParseResume = async () => {
    if (!resumeData.resume) {
      console.error("No resume file found");
      return;
    }

    if (!userEmail) {
      console.error("No user email found");
      return;
    }

    setIsLoading(true);

    setLoadingProgress(0);
    setLoadingMessage("");

    try {
      const parsedData = await parseResume(resumeData.resume, userEmail, {
        onProgress: (event) => {
          setLoadingProgress(event.progress || 0);

          setLoadingMessage(event.message);
        },

        onComplete: (data) => {
          setLoadingProgress(100);

          setLoadingMessage("Parsing complete");
        },

        onError: (message) => {
          console.error(message);

          toast.error(message);
        },
      });

      try {
        setResumeData(parsedData);

        setUserData({
          ...userData,

          resumeData: {
            ...userData.resumeData,

            [parsedData.metadata.name]: {
              ...parsedData,
            },
          },
        });
      } catch (error) {
        console.error("Resume parsing error");

        toast.info(
          "We couldn't fully parse your resume — you can fill the rest in manually.",
        );

        handleErrors("Parsing Resume - Saving parsed resume", error);
      }
    } catch (error) {
      console.error(`Resume parsing error: ${error.message}`);

      toast.error(
        `${getUserMessage(error)} You can also fill in your information manually.`,
      );

      handleErrors("Parsing Resume", error);
    } finally {
      navigateTo("personalInfo", true);

      setIsLoading(false);

      setLoadingProgress(0);
    }
  };

  const handleTailorResume = async (
    selectedResume = null,
    appDetails = applicationDetails,
    jobDesc = jobDescription,
  ) => {
    setIsLoading(true);
    setTailoredResume(null);
    setTailoredResumeDocx(null);
    setTailoredChanges(null);

    try {
      if (
        !["title", "department", "company", "location"].every(
          (key) => key in appDetails,
        )
      ) {
        const extractedDetails = await handleGetJobDetailsFromText(jobDesc);
        appDetails = {
          ...appDetails,
          ...extractedDetails,
        };
      }

      setApplicationDetails(appDetails);

      if (!appDetails.description) {
        appDetails = {
          ...appDetails,
          description: jobDesc,
        };
      }

      const parseScore = (value) => {
        const parsed = Number.parseFloat(String(value ?? "").replace("%", ""));
        return Number.isFinite(parsed) ? parsed : 0;
      };

      const baselineAtsScore =
        appDetails.matchScores?.[selectedResume] ??
        appDetails.matchScore ??
        appDetails.ats ??
        0;

      const result = await generateTailoredResume(
        userEmail,
        appDetails.description,
        appDetails.title,
        selectedResume,
        baselineAtsScore,
      );
      const ats = Math.max(
        parseScore(result.ATS_score),
        parseScore(baselineAtsScore),
      );
      const matchScore = Math.max(
        parseScore(appDetails.matchScores?.[selectedResume]),
        parseScore(appDetails.matchScore),
        parseScore(baselineAtsScore),
      );

      const docxResume = result.tailored_resume_docx_base64 || null;
      setTailoredResume(result.tailored_resume_base64);
      setTailoredResumeDocx(docxResume);
      setAts(ats);
      setApplicationDetails({
        ...appDetails,
        ats: ats,
        matchScore: matchScore,
        resumeDocx: docxResume,
        resumePdf: result.tailored_resume_base64,
        resumeFormat: result.tailored_resume_format || "docx",
        resumeMimeType: docxResume ? DOCX_MIME_TYPE : PDF_MIME_TYPE,
      });

      const formattedChanges = result.changes
        .map((item) => `• ${item.change}\n  Reason: ${item.reason}`)
        .join("\n\n");
      setTailoredChanges(formattedChanges);
      return {
        tailoredData: result || "",
        details: {
          ...appDetails,
          ats: ats,
          matchScore: matchScore,
          resumeDocx: docxResume,
          resumePdf: result.tailored_resume_base64,
          resumeFormat: result.tailored_resume_format || "docx",
          resumeMimeType: docxResume ? DOCX_MIME_TYPE : PDF_MIME_TYPE,
        },
      };
    } catch (error) {
      console.error("Tailor resume error");
      handleErrors("Tailoring Resume", error);
      return { tailoredData: null, details: null };
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadPdf = async (
    save = false,
    resume = tailoredResumeDocx || tailoredResume,
    appDetails = applicationDetails,
    jobUrl = customJobUrl,
    formatOverride = null,
  ) => {
    if (!resume) {
      console.error("No resume data available");
      return;
    }

    try {
      const savedName = appDetails.resume_name || appDetails.resumeName || "";
      const explicitFormat =
        appDetails.resume_format || appDetails.resumeFormat;
      const format =
        formatOverride ||
        explicitFormat ||
        (savedName.toLowerCase().endsWith(".docx") ? "docx" : "") ||
        (resume === tailoredResumeDocx && tailoredResumeDocx ? "docx" : "pdf");
      const mimeType =
        appDetails.resume_mime_type ||
        appDetails.resumeMimeType ||
        (format === "docx" ? DOCX_MIME_TYPE : PDF_MIME_TYPE);
      const extension = format === "docx" ? "docx" : "pdf";
      const dataUri = `data:${mimeType};base64,${resume}`;

      // Fetch it as a blob
      const response = await fetch(dataUri);
      const blob = await response.blob();

      // Download
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      const title = appDetails.title?.replace(" ", "_") || "";
      const department =
        appDetails.department?.split(",")[0].replace(" ", "_") || "";
      const company =
        appDetails.company?.split(",")[0].replace(" ", "_") ||
        appDetails.location?.split(",")[0].replace(" ", "_") ||
        "";
      var fileIdentifier = `${title}${department === "" ? "_" + department : ""}${company === "" ? "_" + company : ""}`;

      if (fileIdentifier.length > 55) {
        fileIdentifier = `${title}${company === "" ? "_" + company : ""}`;
      }
      if (fileIdentifier.length > 55) {
        fileIdentifier = title;
      }

      fileIdentifier = fileIdentifier
        .replace(/[^a-zA-Z0-9]/g, "_")
        .replace(/__+/g, "_")
        .replace(/^_+|_+$/g, "");
      const filename = `Resume_${fileIdentifier}.${extension}`;
      link.download = filename;
      link.click();

      const downloadedApplicationDetails = {
        ...appDetails,
        resumeName: filename || "",
        resume: resume,
        resumeMimeType: mimeType,
        resumeFormat: extension,
        application_type: "manual",
        tailored: true,
        tailoredAt: new Date(),
      };
      setApplicationDetails(downloadedApplicationDetails);

      if (save) {
        await handleSaveJobApplication(downloadedApplicationDetails);
      }

      return downloadedApplicationDetails;
    } catch (error) {
      console.error("Error saving tailored resume");
      throw error;
    }
  };

  const handleDownloadCoverLetter = async (
    resume = tailoredResume,
    jobDesc = jobDescription,
    appDetails = applicationDetails,
  ) => {
    try {
      setIsGeneratingCoverLetter(true);
      let cover_letter = appDetails.cover_letter_used;
      if (cover_letter === undefined || cover_letter === null) {
        cover_letter = await generateCoverLetter(
          userEmail,
          resume,
          jobDesc,
          appDetails.title,
          appDetails.company,
        );
      }

      const dataUri = `data:application/pdf;base64,${cover_letter}`;

      // Fetch it as a blob
      const response = await fetch(dataUri);
      const blob = await response.blob();

      // Download
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      const title = appDetails.title?.replace(" ", "_") || "";
      const department =
        appDetails.department?.split(",")[0].replace(" ", "_") || "";
      const company =
        appDetails.company?.split(",")[0].replace(" ", "_") ||
        appDetails.location?.split(",")[0].replace(" ", "_") ||
        "";
      var fileIdentifier = `${title}${department === "" ? "_" + department : ""}${company === "" ? "_" + company : ""}`;

      if (fileIdentifier.length > 55) {
        fileIdentifier = `${title}${department === "" ? "_" + department : ""}`;
      }
      if (fileIdentifier.length > 55) {
        fileIdentifier = title;
      }

      fileIdentifier = fileIdentifier
        .replace(/[^a-zA-Z0-9]/g, "_")
        .replace(/__+/g, "_")
        .replace(/^_+|_+$/g, "");
      const filename = `Cover_letter_${fileIdentifier}.pdf`;
      link.download = filename;
      link.click();

      await handleSaveJobApplication({
        ...appDetails,
        coverLetterName: filename,
        coverLetter: cover_letter,
      });
      return {
        ...appDetails,
        cover_letter_name: filename,
        cover_letter_used: cover_letter,
      };
    } catch (error) {
      console.error("Error downloading cover letter");
      handleErrors("Download Cover Letter", error);
      toast.error(getUserMessage(error));
    } finally {
      setIsGeneratingCoverLetter(false);
    }
  };

  const handleDeleteResume = async (selectedResume) => {
    try {
      // Delete selected resume
      await deleteResume(userEmail, selectedResume);
    } catch (error) {
      handleErrors("Deleting Resume", error);
    }
  };

  const resetTailorState = () => {
    setTailoredResume(null);
    setTailoredResumeDocx(null);
    setTailoredChanges(null);
    setJobDescription("");
    setApplicationDetails({});
    setIsLoading(false);
  };

  // ============================================
  // USER PROFILE HANDLERS
  // ============================================

  const handleSaveProfile = async (profileData = {}) => {
    setIsLoading(true);

    try {
      const result = await saveProfile(userEmail, profileData);

      if (result.success) {
        // Update local state with combined data
        setUserData((prev) => ({
          ...prev,
          profileData: {
            ...prev.profileData,
            ...profileData,
          },
        }));

        return true;
      } else {
        throw new Error(result.error || "Failed to save profile");
      }
    } catch (error) {
      console.error("Error saving profile");
      toast.error(getUserMessage(error));
      handleErrors("Saving profile", error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveSubscription = async (subscriptionData = {}) => {
    setIsLoading(true);

    try {
      const result = await saveSubscription(userEmail, subscriptionData);

      if (result.success) {
        return true;
      } else {
        throw new Error(result.error || "Failed to save subscription");
      }
    } catch (error) {
      console.error("Error saving subscription");
      handleErrors("Saving subscription", error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    setIsLoading(true);

    try {
      await cancelSubscription(userEmail);

      return true;
    } catch (error) {
      console.error("Error canceling subscription");
      handleErrors("Canceling subscription", error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveResumeData = async (resumeInfo = {}, oldResumeName = "") => {
    setIsLoading(true);

    try {
      const result = await saveResumeData(userEmail, resumeInfo, oldResumeName);
      if (result.success) {
        // Update local state with combined data
        setUserData((prev) => ({
          ...prev,
          resume_data: resumeInfo,
        }));

        setResumeData({ ...resumeData, ...resumeInfo });

        return true;
      } else {
        throw new Error(result.error || "Failed to save resume data");
      }
    } catch (error) {
      console.error("Error saving resume data");
      handleErrors("Saving Resume", error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadAllData = async () => {
    try {
      const profileData = await getUserProfile(
        userEmail,
        true,
        true,
        false,
        false,
      );
      const applicationHistory = await getAllApplications(userEmail);

      // Converting to base64 JSON
      const jsonString = JSON.stringify({ profileData, applicationHistory });
      const base64Data = btoa(jsonString);
      const dataUri = `data:application/json;base64,${base64Data}`;

      // Fetch it as a blob
      const response = await fetch(dataUri);
      const blob = await response.blob();

      // Download
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      const filename = `ApplicantData_${userEmail}.json`;
      link.download = filename;
      link.click();
    } catch (error) {
      console.error("Error downloading all user data");
      handleErrors("Download all user data", error);
    }
  };

  const handleSaveJobPreferences = async (jobPreferences = {}) => {
    setIsLoading(true);

    try {
      const result = await saveJobPreferences(userEmail, jobPreferences);

      if (result.success) {
        // Update local state with combined data
        setUserData((prev) => ({
          ...prev,
          job_preferences: jobPreferences,
        }));

        return true;
      } else {
        throw new Error(result.error || "Failed to save job preferences");
      }
    } catch (error) {
      console.error("Error saving job preferences");
      handleErrors("Saving job preferences", error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateCheckoutSession = async (tier) => {
    try {
      const session = await createCheckoutSession(userEmail, tier);
      return session;
    } catch (error) {
      throw error;
    }
  };

  const deleteAccount = async () => {
    setIsLoading(true);
    try {
      const user = await deleteAllData(userEmail);
      return user;
    } catch (error) {
      handleErrors("Account deletion", error);
      throw error;
    } finally {
      setIsLoading(true);
    }
  };

  // Screen Router
  const renderScreen = () => {
    if (currentScreen === null) {
      return (
        <div className="h-full-with-padding flex items-center justify-center bg-loading-color">
          <img
            src="/assets/Applicant-loading.png"
            className="w-25 h-25 max-auto mb-2"
            alt="Applicant loading logo"
          />
        </div>
      );
    }
    switch (currentScreen) {
      case "login":
        return (
          <LoginScreen
            onRegister={handleRegister}
            onLogin={handleLogin}
            authError={authError}
            isLoading={isLoading}
            verifyCode={verifyCode}
            sendVerificationEmail={sendVerificationEmail}
            navigateTo={navigateTo}
          />
        );
      case "start":
        return (
          <StartScreen
            resumeData={resumeData}
            setResumeData={setResumeData}
            userEmail={userEmail}
            handleResumeUpload={handleResumeUpload}
            handleParseResume={handleParseResume}
            loadingProgress={loadingProgress}
            loadingMessage={loadingMessage}
            isLoading={isLoading}
            navigateTo={navigateTo}
            navigateBack={navigateBack}
          />
        );
      case "personalInfo":
        return (
          <PersonalInfoScreen
            userData={userData}
            setUserData={setUserData}
            userEmail={userEmail}
            resumeData={resumeData}
            setResumeData={setResumeData}
            setResumeExists={setResumeExists}
            navigateTo={navigateTo}
            handleSaveResumeData={handleSaveResumeData}
            refreshMatchScores={refreshMatchScores}
            setIsRefreshingScores={setIsRefreshingScores}
            navigateBack={navigateBack}
            handleErrors={handleErrors}
          />
        );
      case "settings":
        return (
          <SettingsScreen
            userData={userData}
            setUserData={setUserData}
            navigateTo={navigateTo}
            setJobList={setJobList}
            sampleJobs={sampleJobs}
            handleSaveJobPreferences={handleSaveJobPreferences}
          />
        );
      case "home":
        return (
          <HomeScreen
            navigateTo={navigateTo}
            jobList={jobList}
            userEmail={userEmail}
            subscriptionCounts={subscriptionCounts}
            applicationHistory={applicationHistory}
            isLoadingApplicationHistory={isLoadingApplicationHistory}
            loadSavedJobs={loadSavedJobs}
            userStats={userStats}
            setJobDescription={setJobDescription}
            handleErrors={handleErrors}
          />
        );
      case "yourJobs":
        return (
          <YourJobsScreen
            navigateTo={navigateTo}
            jobList={jobList}
            setJobList={setJobList}
            handleJobApplication={handleJobApplication}
            userEmail={userEmail}
            showJobUrlPopup={showJobUrlPopup}
            setShowJobUrlPopup={setShowJobUrlPopup}
            customJobUrl={customJobUrl}
            setCustomJobUrl={setCustomJobUrl}
            jobValidation={jobValidation}
            isValidating={isValidating}
            isLoading={isLoading}
            handleJobUrlValidation={handleJobUrlValidation}
            handleTailorResume={handleTailorResume}
            handleDownloadPdf={handleDownloadPdf}
            resetJobUrlPopup={resetJobUrlPopup}
            resumeData={resumeData}
            setResumeData={setResumeData}
            loadSavedJobs={loadSavedJobs}
            setSavedJobCompanies={setSavedJobCompanies}
            savedJobCompanies={savedJobCompanies}
            isRefreshingScores={isRefreshingScores}
            subscriptionCounts={subscriptionCounts}
            setSubscriptionCounts={setSubscriptionCounts}
            setJobDescription={setJobDescription}
            isLoadingJobs={isLoadingJobs}
            handleErrors={handleErrors}
          />
        );
      case "history":
        return (
          <ApplicationHistoryScreen
            userEmail={userEmail}
            getAllApplications={getAllApplications}
            applicationHistory={applicationHistory}
            setApplicationHistory={setApplicationHistory}
            handleSaveJobApplication={handleSaveJobApplication}
            handleDeleteApplication={handleDeleteApplication}
            handleDownloadPdf={handleDownloadPdf}
            handleDownloadCoverLetter={handleDownloadCoverLetter}
            subscriptionCounts={subscriptionCounts}
            setUserStats={setUserStats}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
            isGeneratingCoverLetter={isGeneratingCoverLetter}
            navigateTo={navigateTo}
            handleErrors={handleErrors}
          />
        );
      case "tailor":
        return (
          <TailorResumeScreen
            tailoredResume={tailoredResume}
            tailoredResumeDocx={tailoredResumeDocx}
            ats={ats}
            tailoredChanges={tailoredChanges}
            jobDescription={jobDescription}
            setJobDescription={setJobDescription}
            isLoading={isLoading}
            isGeneratingCoverLetter={isGeneratingCoverLetter}
            handleTailorResume={handleTailorResume}
            handleDownloadCoverLetter={handleDownloadCoverLetter}
            handleDownloadPdf={handleDownloadPdf}
            handleSaveJobApplication={handleSaveJobApplication}
            subscriptionCounts={subscriptionCounts}
            setSubscriptionCounts={setSubscriptionCounts}
            userEmail={userEmail}
            navigateTo={navigateTo}
            resetTailorState={resetTailorState}
            handleErrors={handleErrors}
          />
        );
      case "profile":
        return (
          <ProfileScreen
            userData={userData}
            setUserData={setUserData}
            setResumeData={setResumeData}
            handleSaveProfile={handleSaveProfile}
            handleSaveResumeData={handleSaveResumeData}
            handleDownloadAllData={handleDownloadAllData}
            deleteAccount={deleteAccount}
            handleLogout={handleLogout}
            deleteResume={handleDeleteResume}
            refreshMatchScores={refreshMatchScores}
            setIsRefreshingScores={setIsRefreshingScores}
            userEmail={userEmail}
            handleErrors={handleErrors}
            navigateTo={navigateTo}
          />
        );
      case "help":
        return (
          <HelpScreen
            userData={userData}
            userEmail={userEmail}
            navigateTo={navigateTo}
            handleErrors={handleErrors}
            isAuthenticated={isAuthenticated}
            currentScreen={currentScreen}
          />
        );
      case "reset":
        return (
          <ResetPasswordScreen
            navigateTo={navigateTo}
            handleErrors={handleErrors}
          />
        );
      case "terms":
        return (
          <LegalScreen
            type="terms"
            navigateBack={navigateBack}
            handleErrors={handleErrors}
          />
        );
      case "privacy":
        return (
          <LegalScreen
            type="privacy"
            navigateBack={navigateBack}
            navigateTo={navigateTo}
            handleErrors={handleErrors}
          />
        );
      case "subscriptionPlans":
        return (
          <SubscriptionPlansScreen
            userEmail={userEmail}
            subscriptionCounts={subscriptionCounts}
            setSubscriptionCounts={setSubscriptionCounts}
            handleCreateCheckoutSession={handleCreateCheckoutSession}
            handleSaveSubscription={handleSaveSubscription}
            handleCancelSubscription={handleCancelSubscription}
            navigateBack={navigateBack}
            handleLogout={handleLogout}
            navigateTo={navigateTo}
            handleErrors={handleErrors}
          />
        );
      case "referral":
        return (
          <ReferralScreen
            handleErrors={handleErrors}
            navigateTo={navigateTo}
            isAuthenticated={isAuthenticated}
            currentScreen={currentScreen}
          />
        );
      case "features":
        return <FeaturesScreen navigateTo={navigateTo} />;
      case "skillGap":
        return <SkillGapScreen userEmail={userEmail} navigateTo={navigateTo} />;
      default:
        return (
          <LoginScreen
            onRegister={handleRegister}
            onLogin={handleLogin}
            authError={authError}
            isLoading={isLoading}
            verifyCode={verifyCode}
            sendVerificationEmail={sendVerificationEmail}
            navigateTo={navigateTo}
            handleErrors={handleErrors}
          />
        );
    }
  };

  return (
    <div className="app-shell">
      {currentScreen !== null &&
        !["login", "", "features"].includes(currentScreen) &&
        !(["help", "referral"].includes(currentScreen) && !isAuthenticated) && (
          <div className="bg-white shadow-sm border-b">
            <div className="max-w-6xl mx-auto px-4 py-2">
              {(currentScreen === "help" ||
                !publicRoutes.includes(currentScreen)) &&
              resumeExists ? (
                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center">
                    <div className="relative inline-flex">
                      <div
                        onClick={() => navigateTo("home")}
                        className="applicant-logo-full cursor-pointer"
                      ></div>
                      <div className="logo-beta-badge"></div>
                    </div>
                  </div>
                  <div className="hamburger-menu">
                    <button
                      onClick={() => setIsMenuOpen(!isMenuOpen)}
                      className="menu-button"
                      aria-label="Menu"
                    >
                      {isMenuOpen ? (
                        <X className="h-6" />
                      ) : (
                        <Menu className="h-6" />
                      )}
                    </button>

                    {/* Dropdown menu */}
                    {isMenuOpen && (
                      <>
                        {/* Backdrop */}
                        <div
                          className="menu-backdrop"
                          onClick={() => setIsMenuOpen(false)}
                        />

                        <div className="menu-panel menu-panel-left">
                          <button
                            onClick={() => {
                              navigateTo("home");
                              setIsMenuOpen(false);
                            }}
                            className="menu-item"
                          >
                            <House className="menu-icon" />
                            Home
                          </button>
                          <button
                            onClick={() => {
                              navigateTo("yourJobs");
                              setIsMenuOpen(false);
                            }}
                            className="menu-item"
                          >
                            <Bookmark className="menu-icon" />
                            Saved Jobs
                          </button>
                          <button
                            onClick={() => {
                              navigateTo("tailor");
                              setIsMenuOpen(false);
                            }}
                            className="menu-item"
                          >
                            <FileUser className="menu-icon" />
                            Tailor Resume
                          </button>

                          <button
                            onClick={() => {
                              navigateTo("profile");
                              setIsMenuOpen(false);
                            }}
                            className="menu-item"
                          >
                            <User className="menu-icon" />
                            Profile
                          </button>

                          <button
                            onClick={() => {
                              navigateTo("history");
                              setIsMenuOpen(false);
                            }}
                            className="menu-item"
                          >
                            <FileText className="menu-icon" />
                            Applications
                          </button>

                          <button
                            onClick={() => {
                              navigateTo("skillGap");
                              setIsMenuOpen(false);
                            }}
                            className="menu-item"
                          >
                            <BrainCircuit className="menu-icon" />
                            My Gaps
                          </button>

                          <button
                            onClick={() => {
                              navigateTo("subscriptionPlans");
                              setIsMenuOpen(false);
                            }}
                            className="menu-item"
                          >
                            <Settings2 className="menu-icon" />
                            Manage Subscription
                          </button>

                          <button
                            onClick={() => {
                              navigateTo("help");
                              setIsMenuOpen(false);
                            }}
                            className="menu-item"
                          >
                            <HelpCircle className="menu-icon" />
                            Help
                          </button>

                          <hr className="menu-divider" />

                          <button
                            onClick={() => {
                              handleLogout();
                              setIsMenuOpen(false);
                            }}
                            className="menu-item menu-item-logout"
                          >
                            <LogOut className="menu-icon" />
                            Log Out
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex justify-between">
                  <div>
                    <div
                      onClick={() => navigateTo("login")}
                      className="applicant-logo-mini cursor-pointer"
                    ></div>
                  </div>
                  {window.history.length > 1 ? (
                    <button
                      onClick={() => {
                        navigateBack();
                      }}
                      className="buttons-navigation"
                    >
                      <ArrowLeft className="w-5 h-5" />
                    </button>
                  ) : (
                    <div>&nbsp;</div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

      <div className="flex-1 flex flex-col">
        {renderScreen()}
        {showErrorReportDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-sm font-semibold text-gray-900">
                  Do you want to report this error?
                </h3>
                <button
                  onClick={() => {
                    setShowErrorReportDialog(false);
                  }}
                  className="buttons-red-mini"
                >
                  <CircleX className="w-5 h-5" />
                </button>
              </div>

              <form
                className="contact-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSendErrorReport();
                  setShowErrorReportDialog(false);
                }}
              >
                <div className="form-group text-sm text-center grid grid-cols-3">
                  {[
                    { label: "Always yes", value: "always_yes" },
                    { label: "Just this once", value: "ask_me_later" },
                    { label: "Never", value: "always_no" },
                  ].map(({ label, value }) => (
                    <div key={value}>
                      <label>{label}</label>
                      <input
                        type="radio"
                        name="errorConsent"
                        checked={errorReportForm[value]}
                        onChange={() =>
                          setErrorReportForm({
                            always_yes: false,
                            ask_me_later: false,
                            always_no: false,
                            [value]: true,
                          })
                        }
                        className="h-4 w-4"
                      />
                    </div>
                  ))}
                </div>
                <div className="buttons-container">
                  <button type="submit" className="buttons-red">
                    Report
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
      <MascotAssistant
        currentScreen={currentScreen}
        isVisible={
          !!isAuthenticated &&
          currentScreen !== null &&
          !["login", "terms", "privacy", "features", "reset"].includes(
            currentScreen,
          )
        }
      />
      {!["login", "terms", "privacy"].includes(currentScreen) && (
        <footer className="legal-footer">
          <p>
            © {new Date().getFullYear()} ApplicantHQ &nbsp;·&nbsp;
            <span className="legal-link" onClick={() => navigateTo("terms")}>
              Terms of Service
            </span>
            &nbsp;·&nbsp;
            <span className="legal-link" onClick={() => navigateTo("privacy")}>
              Privacy Policy
            </span>
          </p>
        </footer>
      )}
    </div>
  );
};

const App = () => (
  <ToastProvider>
    <JobApplicationApp />
  </ToastProvider>
);

export default App;
